import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Col, Container, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  reservationBoardService,
  type CreateBoardInput,
  type ReservationBoard,
} from '../services/reservation-system/reservation-board.service';
import { extractReservationErrorMessage } from '../utils/reservationApiError.util';
import {
  loadCreateBoardCountryCode,
  saveCreateBoardCountryCode,
} from '../utils/reservationBoardFormPrefs.util';
import { reservationBoardSharePath } from '../utils/reservationBoardSharePath.util';
import styles from '../components/reservation-system/ReservationBoardCalendar.module.css';
import RsCountrySelect from '../components/reservation-system/RsCountrySelect';
import { RS_DEFAULT_COUNTRY_CODE, isRsCountryCode, type RsCountryCode } from '../constants/rsCountryCodes';

function formatBoardLastUsed(iso: string | undefined, locale: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(locale);
}

function resolveInitialCountryCode(): RsCountryCode {
  const saved = loadCreateBoardCountryCode();
  if (saved && isRsCountryCode(saved)) {
    return saved;
  }
  return RS_DEFAULT_COUNTRY_CODE;
}

export default function ReservationBoardsManager() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const defaultResourcesInput = useMemo(
    () => t('reservation.manager.defaultResourcesInput', { defaultValue: '項目１\n項目２' }),
    [t, i18n.language]
  );
  const [resourceInput, setResourceInput] = useState(defaultResourcesInput);
  const [countryCode, setCountryCode] = useState<RsCountryCode>(resolveInitialCountryCode);
  const [formError, setFormError] = useState('');

  const [editingBoard, setEditingBoard] = useState<ReservationBoard | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editFormError, setEditFormError] = useState('');

  const [pageMessage, setPageMessage] = useState('');
  const [pageError, setPageError] = useState('');

  const boardsQuery = useQuery({
    queryKey: ['reservation-boards'],
    queryFn: () => reservationBoardService.listBoards(),
  });

  const createMutation = useMutation({
    mutationFn: (input: CreateBoardInput) => reservationBoardService.createBoard(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservation-boards'] });
      setShowCreate(false);
      setName('');
      setDescription('');
      setResourceInput(defaultResourcesInput);
      setFormError('');
    },
    onError: (err) => {
      setFormError(extractReservationErrorMessage(err, t, t('reservation.manager.createFailed', { defaultValue: '作成に失敗しました。' })));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, name: boardName, description: boardDescription }: { id: number; name: string; description: string | null }) =>
      reservationBoardService.updateBoard(id, { name: boardName, description: boardDescription }),
    onSuccess: (updatedBoard) => {
      queryClient.invalidateQueries({ queryKey: ['reservation-boards'] });
      queryClient.setQueryData(['reservation-board', updatedBoard.id], updatedBoard);
      setEditingBoard(null);
      setEditFormError('');
      setPageMessage(t('reservation.manager.updated', { defaultValue: '予約表を更新しました。' }));
      setPageError('');
    },
    onError: (err) => {
      setEditFormError(
        extractReservationErrorMessage(err, t, t('reservation.manager.updateFailed', { defaultValue: '更新に失敗しました。' }))
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => reservationBoardService.deleteBoard(id),
    onSuccess: (_data, boardId) => {
      queryClient.invalidateQueries({ queryKey: ['reservation-boards'] });
      queryClient.removeQueries({ queryKey: ['reservation-board', boardId] });
      setPageMessage(t('reservation.manager.deleted', { defaultValue: '予約表を削除しました。' }));
      setPageError('');
    },
    onError: (err) => {
      setPageError(
        extractReservationErrorMessage(err, t, t('reservation.manager.deleteFailed', { defaultValue: '削除に失敗しました。' }))
      );
      setPageMessage('');
    },
  });

  const handleCreate = () => {
    const resourceNames = resourceInput
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    if (!name.trim()) {
      setFormError(t('reservation.manager.nameRequired', { defaultValue: '名称は必須です。' }));
      return;
    }
    if (resourceNames.length === 0) {
      setFormError(t('reservation.manager.resourceRequired', { defaultValue: '項目を1つ以上入力してください。' }));
      return;
    }
    saveCreateBoardCountryCode(countryCode);
    createMutation.mutate({
      name: name.trim(),
      description: description.trim() || undefined,
      resourceNames,
      countryCode,
    });
  };

  const openEditModal = (board: ReservationBoard) => {
    setEditingBoard(board);
    setEditName(board.name);
    setEditDescription(board.description ?? '');
    setEditFormError('');
  };

  const handleUpdate = () => {
    if (!editingBoard) return;
    if (!editName.trim()) {
      setEditFormError(t('reservation.manager.nameRequired', { defaultValue: '名称は必須です。' }));
      return;
    }
    updateMutation.mutate({
      id: editingBoard.id,
      name: editName.trim(),
      description: editDescription.trim() || null,
    });
  };

  const handleDelete = (board: ReservationBoard) => {
    const confirmed = window.confirm(
      t('reservation.manager.confirmDelete', {
        defaultValue: '「{{name}}」を削除します。予約もすべて削除されます。よろしいですか？',
        name: board.name,
      })
    );
    if (!confirmed) return;
    deleteMutation.mutate(board.id);
  };

  return (
    <Container className="py-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <h1 className="h3 mb-1">{t('reservation.manager.title', { defaultValue: '予約システム' })}</h1>
          <p className="text-muted mb-0">
            {t('reservation.manager.subtitle', { defaultValue: '予約表（ボード）を選んで月間カレンダーで予約します。' })}
          </p>
          <p className="text-muted small mb-0 mt-1">
            {t('reservation.manager.inactiveCleanupNotice', {
              defaultValue:
                '1年間利用のない予約表は、毎日自動的に削除されます（予約データも削除されます）。',
            })}
          </p>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={() => {
            setCountryCode(resolveInitialCountryCode());
            setResourceInput(defaultResourcesInput);
            setShowCreate(true);
          }}>
            {t('reservation.manager.createBoard', { defaultValue: '予約表を作成' })}
          </Button>
        </Col>
      </Row>

      {(pageMessage || pageError) && (
        <Alert
          variant={pageError ? 'danger' : 'success'}
          dismissible
          onClose={() => {
            setPageMessage('');
            setPageError('');
          }}
        >
          {pageError || pageMessage}
        </Alert>
      )}

      {boardsQuery.isLoading && (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" />
        </div>
      )}

      {boardsQuery.isError && (
        <Alert variant="danger">
          {t('reservation.manager.loadFailed', { defaultValue: '予約表の読み込みに失敗しました。' })}
        </Alert>
      )}

      <Row className="g-3">
        {(boardsQuery.data ?? []).map((board) => (
          <Col md={6} lg={4} key={board.id}>
            <Card className="h-100 shadow-sm">
              <Card.Body className="d-flex flex-column">
                <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                  <Card.Title className="mb-0 flex-grow-1">{board.name}</Card.Title>
                  <div className="d-flex flex-shrink-0">
                    <button
                      type="button"
                      className={`btn btn-sm ${styles.iconActionBtn}`}
                      onClick={() => openEditModal(board)}
                      title={t('reservation.manager.editBoardTitle', { defaultValue: '予約表名の編集' })}
                      aria-label={t('reservation.manager.editBoardTitle', { defaultValue: '予約表名の編集' })}
                    >
                      <span className={styles.iconActionEmoji} aria-hidden>
                        ✏️
                      </span>
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${styles.iconActionBtn}`}
                      onClick={() => handleDelete(board)}
                      disabled={deleteMutation.isPending}
                      title={t('reservation.manager.deleteBoardTitle', { defaultValue: '予約表を削除' })}
                      aria-label={t('reservation.manager.deleteBoardTitle', { defaultValue: '予約表を削除' })}
                    >
                      <span className={styles.iconActionEmoji} aria-hidden>
                        🗑️
                      </span>
                    </button>
                  </div>
                </div>
                {board.description && <Card.Text className="text-muted">{board.description}</Card.Text>}
                <Card.Text className="small">
                  {t('reservation.manager.resourceCount', {
                    defaultValue: '項目: {{count}} 件',
                    count: board.resourceNames.length,
                  })}
                </Card.Text>
                {board.lastUsedAt && (
                  <Card.Text className="small text-muted mb-0">
                    {t('reservation.manager.lastUsed', {
                      defaultValue: '最終利用: {{date}}',
                      date: formatBoardLastUsed(board.lastUsedAt, i18n.language) ?? board.lastUsedAt,
                    })}
                  </Card.Text>
                )}
                <div className="mt-auto">
                  <Button
                    as={Link as any}
                    to={reservationBoardSharePath(board.shareUrl) ?? '/reservations'}
                    variant="outline-primary"
                    className="w-100"
                    disabled={!board.shareUrl}
                  >
                    {t('reservation.manager.openBoard', { defaultValue: '予約表を開く' })}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {!boardsQuery.isLoading && (boardsQuery.data?.length ?? 0) === 0 && (
        <Alert variant="info">
          {t('reservation.manager.empty', { defaultValue: '予約表がありません。新規作成してください。' })}
        </Alert>
      )}

      <Modal show={showCreate} onHide={() => setShowCreate(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('reservation.manager.createBoard', { defaultValue: '予約表を作成' })}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError && <Alert variant="danger">{formError}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label>{t('reservation.manager.boardName', { defaultValue: '名称' })}</Form.Label>
            <Form.Control value={name} onChange={(e) => setName(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('reservation.manager.boardDescription', { defaultValue: '説明' })}</Form.Label>
            <Form.Control as="textarea" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('reservation.manager.resources', { defaultValue: '項目（1行1件）' })}</Form.Label>
            <Form.Control as="textarea" rows={4} value={resourceInput} onChange={(e) => setResourceInput(e.target.value)} />
          </Form.Group>
          <RsCountrySelect
            value={countryCode}
            onChange={(next) => {
              setCountryCode(next);
              saveCreateBoardCountryCode(next);
            }}
            t={t}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreate(false)}>
            {t('common.cancel', { defaultValue: 'キャンセル' })}
          </Button>
          <Button variant="primary" onClick={handleCreate} disabled={createMutation.isPending}>
            {t('common.create', { defaultValue: '作成' })}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={editingBoard != null} onHide={() => setEditingBoard(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('reservation.manager.editBoard', { defaultValue: '予約表の編集' })}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editFormError && <Alert variant="danger">{editFormError}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label>{t('reservation.manager.boardName', { defaultValue: '名称' })}</Form.Label>
            <Form.Control value={editName} onChange={(e) => setEditName(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('reservation.manager.boardDescription', { defaultValue: '説明' })}</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditingBoard(null)}>
            {t('common.cancel', { defaultValue: 'キャンセル' })}
          </Button>
          <Button variant="primary" onClick={handleUpdate} disabled={updateMutation.isPending}>
            {t('common.save', { defaultValue: '保存' })}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
